<template>
	<div
		class="premium-shop-item-container"
		:class="`premium-shop-item-container--${offer.attributes?.template}`"
		@click="handleOfferClick"
	>
		<div
			class="premium-shop-item"
			:class="[
				`premium-shop-item--${offer.attributes?.skin}`,
				getPiggyBankClass(offer),
				getProvisionClass(offer),
			]"
		>
			<div class="premium-shop-item__bg">
				<div class="bg--offer-universal" />
			</div>
			<div class="premium-shop-item__header">
				<BoxHeaderBackground slug="premium-shop-item-header" />
				<div class="premium-shop-item__header-text-container">
					<ShrinkText
						:text="translate(offer.name, true)"
						:fontSize="20"
						:maxLineLimit="1"
						hasSameLineHeight
					/>
				</div>

				<div
					v-if="hasPercentageDiscount(offer)"
					class="wax-seal bg-wax-seal--red-66"
				>
					<div class="percentage-discount">
						<span>
							{{ `${offer.attributes.percentage_discount}%` }}
						</span>
						<span class="percentage-discount__label">OFF</span>
					</div>
				</div>

				<div
					v-if="hasPercentageValue(offer)"
					class="wax-seal wax-seal--multi-text bg-wax-seal--red-66"
				>
					<div class="percentage-value">
						<span>
							{{ `${offer.attributes.percentage_value}%` }}
						</span>
						<span class="percentage-value__label">VALUE</span>
					</div>
				</div>

				<div
					v-if="hasEventFlag(offer)"
					class="wax-seal wax-seal--multi-text bg-wax-seal--red-66"
				>
					<div class="event-special">
						<span>Event</span>
						<span class="event-special__label">special</span>
					</div>
				</div>
			</div>

			<div v-if="hasStatus" class="premium-shop-item__sub-header">
				<div class="premium-shop-item__sub-header-status">
					<span>{{ translate("event.activated") }}</span>
				</div>
			</div>

			<span
				v-if="shouldShowOverflowCount"
				class="overflow-total-items-count"
			>
				({{
					replacePlaceholder(
						translate("ui.count_items"),
						"{number}",
						totalItemsCount,
					)
				}})
			</span>

			<OneSlotOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.SLOTS_1"
				:contentData="offer"
			/>

			<TwoSlotOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.SLOTS_2"
				:contentData="offer"
			/>

			<ThreeSlotOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.SLOTS_3"
				:contentData="offer"
			/>

			<FourSlotOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.SLOTS_4"
				:contentData="offer"
			/>

			<EventPassOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.EVENT_PASS"
				:contentData="offer"
			/>

			<RoyalProvisionOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.PROVISION"
				:contentData="offer"
			/>

			<PiggyBankOfferCardDetail
				v-if="offer.attributes.template === OfferTemplate.PIGGY_BANK"
				:contentData="offer"
			/>

			<div
				v-if="offer.attributes.template !== OfferTemplate.PIGGY_BANK"
				class="premium-shop-item__footer"
				:class="{
					'premium-shop-item__footer--purchased': !canPurchase,
				}"
			>
				<div
					v-if="offer.attributes.template === OfferTemplate.PROVISION"
					class="premium-shop-item__footer-provision"
				>
					<div
						v-if="isProvisionActive"
						class="premium-shop-item__footer-timer"
					>
						<Timer
							:time="currentProvisionRemainingTime"
							:iconSize="16"
							:fontSize="16"
							hasIcon
						/>
					</div>
					<div v-else class="premium-shop-item__footer-duration">
						<span v-if="offer.attributes.reward_duration_days">
							{{
								replacePlaceholder(
									translate("ui.number_days"),
									"{number}",
									offer.attributes.reward_duration_days,
								)
							}}
						</span>
					</div>
				</div>
				<div
					v-if="
						offer.attributes.template === OfferTemplate.EVENT_PASS
					"
					class="premium-shop-item__footer-ribbon"
				>
					<span>
						{{ translate("ui.recommended.non_translatable") }}
					</span>
				</div>
				<div
					v-if="showRemainingTime"
					class="premium-shop-item__footer-timer"
				>
					<Timer
						:time="formattedRemainingTime"
						:iconSize="16"
						:fontSize="16"
						hasIcon
						@timerFinished="handleOfferEnd(offer.store_id)"
					/>
				</div>
				<div
					v-if="canPurchase"
					class="premium-shop-item__footer-text-container"
				>
					<span
						v-if="offer.attributes?.limit"
						class="limit"
						:class="{ 'limit--offset': !showRemainingTime }"
					>
						{{ translate("ui.limit") }}
						{{
							`${offer.num_of_purchases}/${offer.attributes.limit}`
						}}
					</span>
				</div>
				<div
					v-if="canPurchase"
					class="premium-shop-item__footer-button"
					@click="$emit('openPopup')"
				>
					<div class="premium-shop-item__footer-button-text">
						<span>{{ formattedPrice }}</span>
					</div>
					<div class="premium-shop-item__footer-button-arrow">
						<NotchedArrow :direction="Placements.RIGHT" />
					</div>
				</div>
				<div
					v-if="!canPurchase"
					class="premium-shop-item__footer-button"
					@click="$emit('openPopup')"
				>
					<div class="premium-shop-item__footer-button-text">
						<span>{{ translate("ui.details") }}</span>
					</div>
					<div class="premium-shop-item__footer-button-arrow">
						<NotchedArrow :direction="Placements.RIGHT" />
					</div>
				</div>
			</div>
		</div>
		<div class="premium-shop-item-decoration">
			<GoldenCorner top left />
			<GoldenCorner top right />
		</div>
	</div>
</template>

<script lang="ts" setup>
import FourSlotOfferCardDetail from "@/components/shop/templates/FourSlotOfferCardDetail.vue";
import OneSlotOfferCardDetail from "@/components/shop/templates/OneSlotOfferCardDetail.vue";
import RoyalProvisionOfferCardDetail from "@/components/shop/templates/RoyalProvisionOfferCardDetail.vue";
import ThreeSlotOfferCardDetail from "@/components/shop/templates/ThreeSlotOfferCardDetail.vue";
import TwoSlotOfferCardDetail from "@/components/shop/templates/TwoSlotOfferCardDetail.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import GoldenCorner from "@/components/SVG/GoldenCorner.vue";
import NotchedArrow from "@/components/SVG/NotchedArrow.vue";
import Timer from "@/components/Timer.vue";
import useQuestStore from "@/store/useQuestStore";
import useShopStore from "@/store/useShopStore.ts";
import type { ActiveProvision } from "@/store/useUserStore.ts";
import useUserStore from "@/store/useUserStore.ts";
import type { QuestActive } from "@/types/quest";
import type { Slot } from "@/types/shop";
import { OfferTemplate, type ShopItem } from "@/types/shop";
import { Placements } from "@/types/ui";
import { convertUnixTimeToRemainingSeconds, formatPrice } from "@/utils/format";
import { roundNumber } from "@/utils/number";
import type { DualPrice } from "@/utils/price.ts";
import { getDualPrice } from "@/utils/price.ts";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import EventPassOfferCardDetail from "../templates/EventPassOfferCardDetail.vue";
import PiggyBankOfferCardDetail from "../templates/PiggyBankOfferCardDetail.vue";

const emit = defineEmits(["openPopup"]);

interface Props {
	offer: ShopItem;
}

const { offer } = defineProps<Props>();

const questStore = useQuestStore();
const userStore = useUserStore();
const shopStore = useShopStore();
const { getActiveQuests: activeQuests } = storeToRefs(questStore);
const { activeProvisions } = storeToRefs(userStore);
const { welcomeBackOffer } = storeToRefs(shopStore);

const canPurchase = computed((): boolean => {
	if (offer.attributes?.limit) {
		return offer.num_of_purchases < offer.attributes.limit;
	} else if (offer.attributes?.template === OfferTemplate.PROVISION) {
		return !activeProvisions.value.some(
			(p: ActiveProvision): boolean => p.storeId === offer.store_id,
		);
	} else {
		// in case of unlimited offer
		return true;
	}
});

const showRemainingTime = computed((): boolean => {
	if (offer.attributes.template === OfferTemplate.PROVISION) return false;
	if (offer.attributes?.hide_remaining_time) return false;
	return !!offer.end_date && formattedRemainingTime.value > 0;
});

const formattedRemainingTime = computed((): number => {
	return convertUnixTimeToRemainingSeconds(
		roundNumber(new Date(offer.end_date as string).getTime() / 1_000),
	);
});

const dualPrice = computed((): DualPrice => {
	return getDualPrice(offer.currency || "", offer.retail_price || 0);
});

const formattedPrice = computed((): string => {
	return formatPrice(dualPrice.value[0].value, dualPrice.value[0].currency);
});

const handleOfferClick = (): void => {
	if (offer.attributes.template === OfferTemplate.PIGGY_BANK) {
		emit("openPopup");
	}
};

const isProvisionActive = computed((): boolean => {
	return activeProvisions.value.some(
		(p: ActiveProvision): boolean => p.storeId === offer.store_id,
	);
});

const currentProvisionRemainingTime = computed((): number => {
	const activeProvision = activeProvisions.value.find(
		(p: ActiveProvision): boolean => p.storeId === offer.store_id,
	);

	if (!activeProvision?.expiresAt) return 0;

	const expiresAtMs = new Date(activeProvision.expiresAt).getTime();
	if (Number.isNaN(expiresAtMs)) return 0;

	const diffInSeconds = (expiresAtMs - Date.now()) / 1000;
	return Math.max(0, Math.floor(diffInSeconds));
});

const hasStatus = computed((): boolean => {
	if (
		offer.attributes.template === OfferTemplate.PROVISION &&
		isProvisionActive.value
	)
		return true;
	return false;
});

const hasPercentageDiscount = (offer: ShopItem): boolean => {
	return (
		!!offer.attributes.percentage_discount &&
		offer.attributes.percentage_discount > 0
	);
};

const hasPercentageValue = (offer: ShopItem): boolean => {
	return (
		!!offer.attributes.percentage_value &&
		offer.attributes.percentage_value > 0
	);
};

const hasEventFlag = (offer: ShopItem): boolean => {
	return !!offer.attributes?.skin && offer.attributes.skin.includes("event");
};

const totalItemsCount = computed((): number => {
	const attributes = offer?.attributes;

	if (
		attributes &&
		"slots" in attributes &&
		Array.isArray(attributes.slots)
	) {
		return attributes.slots.reduce((total: number, slot: Slot): number => {
			const itemCount = slot?.items?.length ?? 0;
			return total + itemCount;
		}, 0);
	}

	return 0;
});

const shouldShowOverflowCount = computed((): boolean => {
	const hasMinItems = totalItemsCount.value > 4;
	const isNotSingleSlot =
		offer?.attributes?.template !== OfferTemplate.SLOTS_1;

	return hasMinItems && isNotSingleSlot;
});

const getPiggyBankClass = (offer: ShopItem): string => {
	// 1. Verified templates
	if (offer.attributes?.template !== OfferTemplate.PIGGY_BANK) {
		return "";
	}

	// 2. Finding the relevant quest
	const quest = activeQuests.value.find(
		(q: QuestActive): boolean =>
			"activity" in offer.attributes &&
			q.id === offer.attributes!.activity,
	);

	// 3. Progress calculation and fulfillment verification
	if (!quest?.objective?.amount) {
		return "is-not-completed";
	}

	const isCompleted = +quest.progress >= +quest.objective.amount;

	return `premium-shop-item--${offer.attributes?.skin + (isCompleted ? "--full" : "--progress")}`;
};

const getProvisionClass = (offer: ShopItem): string => {
	if (offer.attributes?.template !== OfferTemplate.PROVISION) {
		return "";
	}

	return `premium-shop-item--${offer.attributes?.skin}--${isProvisionActive.value ? "active" : "default"}`;
};

const handleOfferEnd = (storeId?: string): void => {
	// In case LB is hidden
	if (storeId === welcomeBackOffer.value?.store_id) {
		useUserStore().setDeeplinkWelcomeBackOfferExpires(null);
		return;
	}
};
</script>

<style lang="scss" scoped>
.premium-shop-item {
	position: relative;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	border-width: 0.05rem;
	border-style: solid;
	border-image-slice: 1;
	background: linear-gradient(
		0deg,
		rgba(11, 63, 78, 1) 0%,
		rgba(52, 159, 191, 1) 100%
	);
	@include flex-col-center;
	width: 100%;
	height: 100%;

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		z-index: 0;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: repeat center;
		@include background(
			url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
			40%,
			center,
			repeat
		);
		width: 100%;
		height: 100%;
		content: "";
	}

	&--skin_01,
	&--event_skin_easter_03,
	&--event_skin_easter_07,
	&--event_skin_easter_11,
	&--event_skin_fishing_03,
	&--event_skin_fishing_05,
	&--skin_people_02,
	&--skin_coin_02,
	&--event_skin_solarfair_02,
	&--regular_starter_skin_golden,
	&--event_skin_hunting_04,
	&--event_skin_halloween_09,
	&--event_skin_pirates_07,
	&--event_skin_pirates_09,
	&--skin_christmas_01 {
		background: linear-gradient(
			0deg,
			rgba(108, 64, 38, 1) 0%,
			rgba(198, 153, 57, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #751617 !important;
				}
			}
		}
	}

	&--skin_02,
	&--event_skin_easter_02,
	&--event_skin_easter_06,
	&--event_skin_easter_10,
	&--event_skin_fishing_02,
	&--event_skin_fishing_07,
	&--skin_resource_02,
	&--skin_level_up_02,
	&--event_skin_firefest_01,
	&--event_skin_firefest_11,
	&--event_skin_solarfair_07,
	&--event_skin_solarfair_11,
	&--regular_starter_skin_01,
	&--event_skin_hunting_11,
	&--event_skin_pirates_06,
	&--event_skin_pirates_11 {
		background: linear-gradient(
			0deg,
			rgba(19, 70, 85, 1) 0%,
			rgba(63, 154, 180, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #893d0c !important;
				}
			}
		}
	}

	&--skin_03,
	&--event_pass_skin_firefest_01,
	&--regular_starter_skin_02,
	&--event_pass_skin_easter_01,
	&--event_pass_skin_fishing_01,
	&--event_pass_skin_hunting_01,
	&--event_pass_skin_halloween_01 {
		background: linear-gradient(
			0deg,
			rgba(53, 38, 71, 1) 0%,
			rgba(117, 97, 175, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #ac3173 !important;
				}
			}
		}
	}

	&--skin_04,
	&--event_skin_fishing_04,
	&--event_skin_fishing_06,
	&--skin_exploration_01,
	&--event_skin_firefest_04,
	&--event_skin_firefest_06,
	&--regular_starter_skin_03 {
		background: linear-gradient(
			0deg,
			rgba(28, 74, 66, 1) 0%,
			rgba(34, 181, 145, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #bc4b1e !important;
				}
			}
		}
	}

	&--skin_05,
	&--skin_storage_01,
	&--skin_gem_02,
	&--event_skin_firefest_12,
	&--regular_starter_skin_04,
	&--event_skin_halloween_07,
	&--event_skin_halloween_11,
	&--event_skin_pirates_08,
	&--skin_christmas_06,
	&--skin_christmas_08 {
		background: linear-gradient(
			0deg,
			rgba(32, 32, 32, 1) 0%,
			rgba(94, 75, 72, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #751617 !important;
				}
			}
		}
	}

	&--skin_06,
	&--event_skin_fishing_01,
	&--event_skin_fishing_08,
	&--skin_gem_01,
	&--event_skin_firefest_08,
	&--event_skin_solarfair_01,
	&--regular_starter_skin_05,
	&--event_skin_hunting_10,
	&--event_skin_halloween_01,
	&--event_skin_halloween_12,
	&--event_skin_pirates_01 {
		background: linear-gradient(
			0deg,
			rgba(125, 58, 53, 1) 0%,
			rgba(225, 131, 86, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #097e91 !important;
				}
			}
		}
	}

	&--skin_07,
	&--regular_starter_skin_06 {
		background: linear-gradient(
			0deg,
			rgba(71, 93, 27, 1) 0%,
			rgba(128, 159, 28, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #743e91 !important;
				}
			}
		}
	}

	&--skin_08,
	&--event_skin_firefest_02,
	&--event_skin_solarfair_03,
	&--regular_starter_skin_07,
	&--event_skin_hunting_12,
	&--event_skin_halloween_04 {
		background: linear-gradient(
			0deg,
			rgba(62, 44, 65, 1) 0%,
			rgba(162, 68, 141, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #ad2e34 !important;
				}
			}
		}
	}

	&--skin_09,
	&--skin_resource_01,
	&--skin_level_up_01,
	&--event_skin_solarfair_08,
	&--regular_starter_skin_08,
	&--event_skin_hunting_01,
	&--event_skin_hunting_06 {
		background: linear-gradient(
			0deg,
			rgba(108, 64, 38, 1) 0%,
			rgba(198, 153, 57, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #014458 !important;
				}
			}
		}
	}

	&--skin_10,
	&--skin_storage_02,
	&--skin_food_02,
	&--event_skin_firefest_05,
	&--event_skin_firefest_10,
	&--event_skin_solarfair_05,
	&--event_skin_solarfair_10,
	&--event_skin_hunting_02,
	&--event_skin_hunting_07,
	&--event_skin_halloween_02,
	&--event_skin_halloween_06,
	&--event_skin_pirates_02,
	&--skin_christmas_04 {
		background: linear-gradient(
			0deg,
			rgba(53, 77, 7, 1) 0%,
			rgba(104, 133, 43, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #703701 !important;
				}
			}
		}
	}

	&--skin_11,
	&--skin_exploration_02,
	&--event_skin_firefest_07,
	&--event_skin_hunting_09,
	&--event_skin_halloween_05 {
		background: linear-gradient(
			0deg,
			rgba(32, 32, 32, 1) 0%,
			rgba(94, 75, 72, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #422a77 !important;
				}
			}
		}
	}

	&--skin_12,
	&--skin_people_01 {
		background: linear-gradient(
			0deg,
			rgba(41, 68, 108, 1) 0%,
			rgba(91, 140, 201, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #573701 !important;
				}
			}
		}
	}

	&--skin_13,
	&--skin_food_01,
	&--skin_building_02,
	&--event_skin_firefest_03,
	&--event_skin_firefest_09,
	&--event_skin_solarfair_09,
	&--event_skin_hunting_03,
	&--event_skin_halloween_08,
	&--event_skin_pirates_03,
	&--event_skin_pirates_05,
	&--event_skin_pirates_12,
	&--skin_christmas_03,
	&--skin_christmas_07 {
		background: linear-gradient(
			0deg,
			rgba(109, 47, 13, 1) 0%,
			rgba(226, 123, 72, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #2a2962 !important;
				}
			}
		}
	}

	&--skin_14,
	&--skin_coin_01,
	&--event_skin_solarfair_06,
	&--event_skin_hunting_05,
	&--skin_christmas_02,
	&--skin_christmas_09 {
		background: linear-gradient(
			0deg,
			rgba(17, 43, 26, 1) 0%,
			rgba(67, 82, 71, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #4b3001 !important;
				}
			}
		}
	}

	&--skin_15,
	&--skin_building_01,
	&--skin_gem_03 {
		background: linear-gradient(
			0deg,
			rgba(11, 72, 84, 1) 0%,
			rgba(62, 192, 216, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #3b3d01 !important;
				}
			}
		}
	}

	&--skin_16,
	&--skin_gem_04,
	&--event_skin_solarfair_04,
	&--event_skin_solarfair_12,
	&--event_skin_hunting_08,
	&--event_skin_halloween_03,
	&--event_skin_halloween_10,
	&--event_skin_pirates_04,
	&--event_skin_pirates_10,
	&--skin_christmas_05,
	&--skin_christmas_10 {
		background: linear-gradient(
			0deg,
			rgba(121, 7, 7, 1) 0%,
			rgba(228, 106, 106, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #016e8e !important;
				}
			}
		}
	}

	&--event_skin_easter_01,
	&--event_skin_easter_05,
	&--event_skin_easter_09 {
		background: linear-gradient(
			0deg,
			rgba(125, 58, 53, 1) 0%,
			rgba(225, 131, 86, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #097e91 !important;
				}
			}
		}
	}

	&--event_skin_easter_04,
	&--event_skin_easter_08,
	&--event_skin_easter_12 {
		background: linear-gradient(
			0deg,
			rgba(71, 93, 27, 1) 0%,
			rgba(128, 159, 28, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #743e91 !important;
				}
			}
		}
	}

	&--skin_black_friday_01,
	&--skin_black_friday_02,
	&--skin_black_friday_03,
	&--skin_black_friday_07 {
		background: linear-gradient(
			0deg,
			rgba(46, 23, 46, 1) 0%,
			rgba(68, 26, 75, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #991bb0 !important;
				}
			}
		}
	}

	&--skin_black_friday_04,
	&--skin_black_friday_05,
	&--skin_black_friday_06,
	&--skin_black_friday_08 {
		background: linear-gradient(
			0deg,
			rgba(66, 37, 71, 1) 0%,
			rgba(192, 52, 217, 1) 100%
		);

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #1a1a1a !important;
				}
			}
		}
	}

	&--piggy_bank_gem_skin_01 {
		&--progress {
			background: linear-gradient(
				0deg,
				rgba(32, 44, 89, 1) 0%,
				rgba(83, 131, 207, 1) 100%
			);

			.premium-shop-item__header {
				:deep(.box-header-bg) {
					.solid-bg-polygon {
						fill: #ac2e35 !important;
					}
				}
			}
		}

		&--full {
			background: linear-gradient(
				0deg,
				rgba(108, 64, 38, 1) 0%,
				rgba(198, 153, 57, 1) 100%
			);

			.premium-shop-item__header {
				:deep(.box-header-bg) {
					.solid-bg-polygon {
						fill: #9c191b !important;
					}
				}
			}
		}
	}

	&--provision_royal_skin_01 {
		background-image: linear-gradient(
			0deg,
			rgba(24, 44, 46, 1),
			rgba(30, 163, 175, 1)
		);
		min-height: 12.25rem;

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #72420c !important;
				}
			}
		}
	}

	&--provision_royal_black_friday_skin_01,
	&--provision_royal_black_friday_skin_02 {
		background-image: linear-gradient(
			0deg,
			rgba(24, 24, 24, 1) 0%,
			rgba(157, 31, 180, 1) 100%
		);
		min-height: 12.25rem;

		.premium-shop-item__header {
			:deep(.box-header-bg) {
				.solid-bg-polygon {
					fill: #72420c !important;
				}
			}
		}
	}

	&-container:not(.premium-shop-item-container--piggy_bank) {
		position: relative;
		width: 100%;

		.overflow-total-items-count {
			padding: 0.25rem 0.75rem;
			width: 100%;
			color: #f2dcb5;
			font-size: 0.75rem;
			line-height: 1;
			text-align: left;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}

	&__bg {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		@include flex-col-center;

		.bg--offer-universal {
			width: 100%;
			height: 100%;
			@include background(
				url(#{$path-images} + "shop/backgrounds/offer-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}

	&__header {
		position: relative;
		flex-grow: 0;
		flex-shrink: 0;
		width: 100%;
		height: 2rem;
		// NOTE: z-index cannot be here

		&-text-container {
			padding-right: 5rem;
		}

		:deep(.box-header-bg) {
			position: relative;
			mix-blend-mode: normal;
			width: 100%;
			height: 2rem;

			.texture-bg-polygon {
				opacity: 0.2 !important;
				filter: invert(1); // must be inverted
			}
		}

		&-text-container {
			@include flex-row-start;
			position: absolute;
			top: 0;
			left: 0;
			padding-left: 1.125rem;
			width: 100%;
			height: 100%;

			.text-container {
				width: 100%;
				@include flex-row-start;
			}

			:deep(.shrink-text) {
				padding-bottom: 0.375rem;
				color: #f4d278;
				font-weight: 800;
				text-align: left;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
			}
		}

		.wax-seal {
			position: absolute;
			top: -0.25rem;
			right: -0.25rem;
			z-index: 2;
			filter: drop-shadow(0 0.188rem 0.75rem rgba(35, 18, 4, 0.6));
			padding: 0.625rem;
			text-align: center;

			.black-friday-wrapper {
				position: relative;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%) rotate(-6deg) !important;
				transform-origin: center;
				padding-bottom: 0.375rem;
				width: 100%;
			}

			span,
			:deep(.text-container) {
				transform: rotate(-6deg);

				span {
					color: #fff;
					font-weight: 800;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					// text-transform: uppercase;
				}

				.black-friday {
					color: #f4d278;
					text-transform: uppercase;

					&-subtext {
						margin-top: 0.125rem;
						padding-left: 0.25rem;
						text-transform: uppercase;
					}
				}
			}

			.percentage-discount {
				height: 100%;
				@include flex-col-center;
				flex-wrap: wrap;
				transform: rotate(-6deg);
				padding-bottom: 0.25rem;
				color: #fff;
				font-weight: 800;
				font-size: 0.875rem;
				line-height: 1;

				&__label {
					font-size: 0.75rem;
				}
			}

			.percentage-value {
				height: 100%;
				@include flex-col-center;
				flex-wrap: wrap;
				transform: rotate(-6deg);
				padding-bottom: 0.25rem;
				color: #fee092;
				font-weight: 800;
				font-size: 0.875rem;
				line-height: 1;

				&__label {
					font-size: 0.75rem;
				}
			}

			.event-special {
				height: 100%;
				@include flex-col-center;
				flex-wrap: wrap;
				transform: rotate(-6deg);
				padding-bottom: 0.25rem;
				color: #fff;
				font-weight: 800;
				font-size: 0.75rem;
				line-height: 1;
				text-transform: uppercase;

				&__label {
					font-size: 0.625rem;
				}
			}
		}
	}

	&__sub-header {
		position: absolute;
		top: 2.375rem;
		@include flex-row-start;
		z-index: 1;
		width: 100%;
		height: 1.25rem;

		&-status {
			position: relative;
			background-image: linear-gradient(
				to right,
				rgba(0, 82, 24, 1),
				rgba(0, 82, 24, 0.8),
				rgba(0, 82, 24, 0.7)
			);
			padding: 0 0.375rem;
			padding-right: 2.25rem;
			width: auto;
			height: 1.625rem;
			@include flex-col-center;
			align-items: flex-start;
			mask-image: linear-gradient(
				to right,
				black 0%,
				black 20%,
				black 70%,
				transparent 98%
			);
			color: #f2dcb5;
			font-weight: 800;
			text-transform: uppercase;

			&::before,
			&::after {
				position: absolute;
				left: 0;
				z-index: 1;
				background: #1bae4a;
				width: 100%;
				height: 0.063rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}

			span {
				padding-bottom: 0.125rem;
			}
		}
	}

	&__footer {
		position: absolute;
		bottom: 0.5rem;
		flex-grow: 0;
		flex-shrink: 0;
		padding-right: 6.375rem;
		width: 100%;
		height: 1.5rem;
		@include flex-row-center;

		&.premium-shop-item__footer--purchased {
			@include flex-row-end;
			align-items: flex-end;
			padding-right: 0.5rem;
		}

		.premium-shop-item__footer-text-container {
			justify-content: flex-start;
		}

		&--purchased {
			@include flex-row-center;
		}

		&-text-container {
			position: relative;
			@include flex-row-center;
			width: 100%;
			height: 100%;

			.limit {
				padding-bottom: 0.125rem;
				color: #fff3dd;
				font-size: 0.625rem;
				text-align: center;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

				&--offset {
					margin-left: 0.75rem;
				}
			}
		}

		&-ribbon {
			position: absolute;
			top: -2.625rem;
			left: -0.5rem;
			width: 9.25rem;
			height: 1.75rem;
			@include background(
				url($path-images + "shop/backgrounds/recommended-left-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
			@include flex-row-center;
			z-index: 2;
			color: #fff3dd;
			font-weight: 800;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		&-timer {
			position: relative;
			background-image: linear-gradient(
				to right,
				rgba(85, 0, 20, 1),
				rgba(85, 0, 20, 0.8),
				rgba(85, 0, 20, 0.7)
			);
			padding: 0 0.375rem;
			padding-right: 2.25rem;
			width: auto;
			height: 1.625rem;
			@include flex-col-center;
			align-items: flex-start;
			mask-image: linear-gradient(
				to right,
				black 0%,
				black 20%,
				black 70%,
				transparent 98%
			);

			&::before,
			&::after {
				position: absolute;
				left: 0;
				z-index: 1;
				background: #cf0032;
				width: 100%;
				height: 0.063rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}

			:deep(.timer__time) {
				padding-bottom: 0.125rem;
				font-size: 1rem;
			}
		}

		&-duration {
			position: relative;
			background-image: linear-gradient(
				to right,
				rgba(181, 124, 24, 1),
				rgba(181, 124, 24, 0.8),
				rgba(181, 124, 24, 0.7)
			);
			padding: 0 0.75rem;
			padding-right: 2.25rem;
			width: 6.875rem;
			height: 1.625rem;
			@include flex-col-center;
			align-items: flex-start;
			mask-image: linear-gradient(
				to right,
				black 0%,
				black 20%,
				black 70%,
				transparent 98%
			);

			&::before,
			&::after {
				position: absolute;
				left: 0;
				z-index: 1;
				background: #f4d278;
				width: 100%;
				height: 0.063rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}

			span {
				padding-bottom: 0.125rem;
				color: #fff3dd;
				font-size: 0.875rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}

		&-button {
			position: absolute;
			top: -0.125rem;
			right: 0.5rem;
			z-index: 2;
			box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
			border-width: 0.1rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			width: auto;
			height: 1.688rem;
			@include flex-row-center;
			@include clickable-element;

			&-text {
				background-color: #392501;
				padding: 0 0.75rem;
				height: 100%;
			}

			&-arrow {
				background-image: linear-gradient(to top, #875806, #dd9a27);
				padding: 0.125rem 0.5rem;
				height: 100%;

				.notched-arrow {
					filter: drop-shadow(
						0.04rem 0.04rem 0.013rem rgba(0, 0, 0, 0.6)
					);
					width: 0.563rem;
					height: 0.813rem;
				}
			}

			span {
				padding-bottom: 0.25rem;
				color: #fff3dd;
				font-weight: 800;
				font-size: 0.75rem;
				text-align: right;
				text-shadow: 0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6);
			}
		}

		.purchased-label {
			@include flex-row-center;
			mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);
			border-top: 0.0625rem solid #1dcf50;
			border-bottom: 0.0625rem solid #1dcf50;
			background: #005218;
			padding-bottom: 0.125rem;
			width: 8rem;
			height: 1.75rem;
			color: #f2dcb5;
			font-weight: 800;
			font-size: 0.875rem;
			text-transform: uppercase;
		}

		&-provision {
			@include flex-col-start;
			flex: 1 1 auto;
			align-items: flex-start;
		}
	}

	&-decoration {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 100%;
		pointer-events: none;

		:deep(.golden-corner) {
			width: 1rem;
			height: 1rem;
		}

		:deep(.golden-corner--top-left) {
			position: absolute;
			top: 0;
			left: 0;
		}

		:deep(.golden-corner--top-right) {
			position: absolute;
			top: 0;
			right: 0;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
	}
}
</style>
